import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch, ApiRequestError } from "../../lib/api.js";
import { useToast } from "../Toast.js";
import { useConfirm } from "../ConfirmDialog.js";
import { useOperationRun } from "../../hooks/useOperationRun.js";
import {
  isTerminalRunStatus,
  isTransportFailure,
  isRunTargetNode,
  type OperationRun,
} from "../../lib/operationRun.js";
import {
  MISSING_ROW_POLICY_LABELS,
  WRITE_MODE_DESCRIPTIONS,
  WRITE_MODE_LABELS,
} from "../../lib/pdoUiLabels.js";
import { LATE_ARRIVING_LABELS, MergeHistoryFieldPickers } from "../pdo/MergeHistoryFieldPickers.js";
import { WithinBatchDupSection, type DupHandling } from "../pdo/WithinBatchDupSection.js";
import { WatermarkBindingSection, type WatermarkBinding } from "./persist/WatermarkBindingSection.js";
import { WatermarkStateSection } from "./persist/WatermarkStateSection.js";
import { PersistRebuildBanner, type RebuildReason } from "./persist/PersistRebuildBanner.js";
import { PersistRunHistory } from "../pdo/PersistRunHistory.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";

type OperationVariableType = "string" | "int" | "decimal" | "bool" | "date" | "datetime";
type OperationVariableDef = {
  name: string;
  type: OperationVariableType;
  value: string | number | boolean | null;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// ADR-0094: 'merge' added; legacy fields removed from the shape.
type WriteMode = "replace_snapshot" | "append" | "merge" | "merge_history";
type PdoStatus = "active" | "draft" | "archived";

const KEYED_WRITE_MODES: ReadonlySet<WriteMode> = new Set([
  "append",
  "merge",
  "merge_history",
]);

/**
 * How often to look for the operation run while the `execute-persist` request is
 * still open. The route answers only once the whole materialization is done, so
 * the run id has to come from the runs list — and it has to be in hand BEFORE a
 * dropped connection, not after.
 */
const RUN_DISCOVERY_POLL_MS = 2000;
/**
 * Give up looking after this many attempts. The hub creates the run row before
 * it starts the work, so it shows up on the first tick or two; anything past
 * that is not going to appear, and the failure path looks once more anyway.
 */
const RUN_DISCOVERY_MAX_ATTEMPTS = 15;
/** How often to re-read a run we are following after losing its request. */
const RUN_FOLLOW_POLL_MS = 3000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { window.setTimeout(resolve, ms); });
}

type PdoConfig = {
  version: "pdo.v1";
  write_mode: WriteMode;
  business_key?: string[];
  business_time?: { source_field: string; fallback: "system_time" | "reject" };
  /** ADR-0094: deny-list of non-key columns to exclude from change detection. */
  change_detection?: { untracked_fields: string[] };
  missing_row_policy?: "ignore_missing" | "close_on_missing";
  late_arriving_policy?: "reject" | "allow_monotonic_only";
  /** ADR-0094: discriminated within-batch duplicate handling. */
  duplicate_key_handling?: DupHandling;
  retention?: {
    max_versions: number | null;
    max_age_days: number | null;
    cold_max_age_days: number | null;
  };
  /** ADR-0103: keyed-mode rollback depth (current + N retained rollback points). */
  rollback_copies?: number;
};

/** ADR-0100 — structured drift row returned by GET /tenant/pdos/:id. */
export type ChangeRow = { field: string; label: string; before: string; after: string };

/**
 * ADR-0100/0102 — drift payload from GET /tenant/pdos/:id.
 *
 * `null` is reserved for "not computed" (caller can't read source op). The
 * inspector treats `null` and `{ has_drift: false }` identically — no banner —
 * but the server-side distinction matters for logging. ADR-0102 retired the
 * `upload_source` reason — every PDO is graph-fed under the universal persist
 * model.
 */
export type SpecDrift =
  | { has_drift: false; reason?: "no_snapshot" | "orphaned" | "invalid_spec_config"; message?: string }
  | { has_drift: true; changes: ChangeRow[] };

type PdoSummary = {
  id: string;
  name: string;
  status: PdoStatus;
  config: PdoConfig;
  current_version: {
    version_number: number;
    row_count: number;
    created_at?: string | null;
  } | null;
  updated_at: string;
  /** ADR-0100 — computed by the hub; null when caller can't read source op. */
  spec_drift?: SpecDrift | null;
  /**
   * Layer 2 (2026-06-10) — single source of truth for run mode across every
   * keyed PDO (with or without a watermark binding). 'initialize' on a fresh
   * PDO; flipped to 'increment' after the first successful INIT. Writable
   * back to 'initialize' via the immediate Re-initialize button (one-shot)
   * or POST .../mark-for-reinitialize (sticky — consumed by next any-path
   * run). Only snapshot policy ignores this field at runtime.
   */
  current_run_mode?: "initialize" | "increment";
};

type MergeAnalysis = {
  write_mode?: string;
  rows_read?: number;
  rows_new?: number;
  rows_changed?: number;
  rows_unchanged?: number;
  rows_closed?: number;
  error?: string;
};

type BuildOverrides = {
  writeMode?: WriteMode;
  primaryKeyFields?: string[];
  eventTimeField?: string;
  businessTimeFallback?: "system_time" | "reject";
  untrackedFields?: string[];
  dupHandling?: DupHandling;
  // ADR-0100 — needed because writes flush on every change (autosave), not
  // onBlur, so the override must carry the new value through React's batched
  // setState that hasn't yet reached `buildConfig`'s closure.
  missingRowPolicy?: "ignore_missing" | "close_on_missing";
  lateArrivingPolicy?: "reject" | "allow_monotonic_only";
  maxVersions?: string;
  maxAgeDays?: string;
  coldMaxAgeDays?: string;
  rollbackCopies?: string;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * ADR-0102 — simplified persist inspector.
 *
 * The persist node is now universal: every PDO is graph-fed. The pre-ADR-0102
 * upload-source branch (three wizard components + an auto-heal effect) was
 * superseded by `OperationInspectorFileProvider` and its dead files were
 * removed by ADR-0192. This inspector only handles graph-source semantics:
 * PdoConfig editing, watermark binding, execution buttons, and the unified
 * `PersistRebuildBanner`.
 */
export function OperationInspectorPersist({
  selectedNode,
  operationId,
  setNodeData,
  operationVariables,
  flushAndSave,
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  /**
   * ADR-0095 — operation spec's variables[]. Passed from OperationEditor so the
   * watermark binding picker can offer orderable-typed variables and the
   * State section can render typed inputs for manual overrides.
   */
  operationVariables: OperationVariableDef[];
  /**
   * Bug fix 2026-06-10 — passed from OperationEditor so Initialize / Re-init /
   * Increment all force pending canvas state (provider SQL edits, etc.) to
   * land in the saved spec before the hub reads it. Without this the hub
   * would race the autosave debounce and run against stale SQL — which looked
   * like a cache hit ("changed the SQL, ran Init, got the old rows") but was
   * really a save-ordering problem.
   */
  flushAndSave: () => Promise<boolean>;
}) {
  const toast = useToast();
  const confirmCtx = useConfirm();
  const runController = useOperationRun();

  const config = selectedNode.data.config ?? {};
  const specPersist = (config.spec_persist as Record<string, unknown> | undefined) ?? {};
  const pdoId = (specPersist.pdo_id as string | null | undefined) ?? null;

  // PDO state
  const [pdo, setPdo] = useState<PdoSummary | null>(null);
  const [pdoLoading, setPdoLoading] = useState(false);

  // Form field state (local — flushed to API on blur / after merge field changes)
  const [writeMode, setWriteMode] = useState<WriteMode>("replace_snapshot");
  const [primaryKeyFields, setPrimaryKeyFields] = useState<string[]>([]);
  const [eventTimeField, setEventTimeField] = useState("");
  const [businessTimeFallback, setBusinessTimeFallback] = useState<"system_time" | "reject">("system_time");
  // ADR-0094: deny-list. Empty = all non-key columns tracked.
  const [untrackedFields, setUntrackedFields] = useState<string[]>([]);
  const [missingRowPolicy, setMissingRowPolicy] = useState<"ignore_missing" | "close_on_missing">("ignore_missing");
  const [lateArrivingPolicy, setLateArrivingPolicy] = useState<"reject" | "allow_monotonic_only">("reject");
  // ADR-0094: discriminated within-batch dup handling (default = error).
  const [dupHandling, setDupHandling] = useState<DupHandling>({ mode: "error" });
  const [maxVersions, setMaxVersions] = useState("");
  const [maxAgeDays, setMaxAgeDays] = useState("");
  const [coldMaxAgeDays, setColdMaxAgeDays] = useState("");
  // ADR-0103: keyed-mode rollback depth (current + N retained rollback points).
  const [rollbackCopies, setRollbackCopies] = useState("");

  const [upstreamColumns, setUpstreamColumns] = useState<string[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsError, setColumnsError] = useState<string | null>(null);

  // Execution state
  const [executing, setExecuting] = useState(false);
  // Server-truth "a run is in flight" — reported by PersistRunHistory (which
  // polls the materialization log). `executing` is local and resets on remount,
  // so a run started before the user navigated away would otherwise leave the
  // controls enabled; this reconciles them. See runActive below.
  const [serverRunActive, setServerRunActive] = useState(false);
  const [mergeAnalysis, setMergeAnalysis] = useState<MergeAnalysis | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Re-entry guard for runPreview — same rationale as runInFlightRef: preview
  // does a real upstream pull now, so a rapid double-click shouldn't fire two.
  const previewInFlightRef = useRef(false);
  // Sticky next-run-mode toggle (non-watermark keyed PDOs). Separate from
  // `executing` so the run buttons keep their own "Running…" labels.
  const [modeBusy, setModeBusy] = useState(false);
  // Synchronous re-entry guard for runExecute. `executing` (state) drives the
  // disabled UI but only after a render; runExecute awaits `flushAndSave()`
  // before that render lands, so a rapid second click can slip through and
  // fire a duplicate materialization (server rejects it with the one-running
  // constraint). The ref blocks re-entry the instant the first click starts.
  const runInFlightRef = useRef(false);
  // Bumped after any run so the WatermarkStateSection re-fetches its own
  // state (its mode pill is fetched independently and would otherwise show
  // a stale mode after the post-INIT flip to increment).
  const [wmRefreshNonce, setWmRefreshNonce] = useState(0);
  // Torn down on unmount so the "follow the run after the browser lost the
  // connection" loop below can never outlive this inspector.
  const disposedRef = useRef(false);
  useEffect(() => {
    disposedRef.current = false;
    return () => { disposedRef.current = true; };
  }, []);
  // ADR-0103: the Retention/Rollback disclosure defaults open for keyed stores
  // (rollback_copies is a primary keyed setting, not an advanced knob) and
  // collapsed for snapshot. null = follow the mode default; a user toggle pins it.
  const [retentionOpen, setRetentionOpen] = useState<boolean | null>(null);

  // ADR-0100: PDO load handles the drift payload + last-execution hint only.
  // The form is hydrated from spec_persist.config (see effect below), NOT from
  // the PDO row's config column. The PDO snapshot is only consulted for the
  // server-side drift diff.
  useEffect(() => {
    if (!pdoId) {
      setPdo(null);
      return;
    }
    void (async () => {
      setPdoLoading(true);
      try {
        const data = await apiFetch<{ pdo: PdoSummary }>(`/api/tenant/pdos/${encodeURIComponent(pdoId)}`);
        setPdo(data.pdo);
      } catch {
        setPdo(null);
      } finally {
        setPdoLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdoId]);

  // Lightweight re-read of the PDO row (drives the PersistRebuildBanner via
  // `pdo.current_run_mode`). Used after out-of-band mode changes from the
  // child WatermarkStateSection ("Switch to INIT") so the INIT rebuild banner
  // appears without a full node reselect. Keeps the existing pdo on a
  // transient error (unlike the mount load, which nulls it).
  const refreshPdo = useCallback(async () => {
    if (!pdoId) return;
    try {
      const data = await apiFetch<{ pdo: PdoSummary }>(`/api/tenant/pdos/${encodeURIComponent(pdoId)}`);
      setPdo(data.pdo);
    } catch {
      /* keep existing pdo on transient error */
    }
  }, [pdoId]);

  // PersistRunHistory polls the run log and reports whether a run is in flight.
  // On the true→false edge a run just finished (possibly one started before the
  // user navigated away, so this instance never saw it complete) — refresh the
  // PDO (current version) and nudge the watermark section.
  const prevRunActiveRef = useRef(false);
  const handleRunActiveChange = useCallback((active: boolean) => {
    setServerRunActive(active);
    if (prevRunActiveRef.current && !active) {
      void refreshPdo();
      setWmRefreshNonce((n) => n + 1);
    }
    prevRunActiveRef.current = active;
  }, [refreshPdo]);

  // ADR-0100: hydrate form from spec_persist.config — the canonical home for
  // persist config under the new model. Runs on node selection and whenever
  // the spec config changes externally (e.g., a different actor updates the
  // operation). Keys the effect on a stable JSON serialization so we re-sync
  // only when the underlying values actually change.
  const specPersistConfigJson = (() => {
    const sp = (selectedNode.data.config?.spec_persist as Record<string, unknown> | undefined) ?? {};
    return JSON.stringify(sp.config ?? null);
  })();
  useEffect(() => {
    const sp = (selectedNode.data.config?.spec_persist as Record<string, unknown> | undefined) ?? {};
    const cfg = sp.config as PdoConfig | undefined;
    syncFormFromSpec(cfg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode.id, specPersistConfigJson]);

  // Reset analysis when node changes
  useEffect(() => {
    setMergeAnalysis(null);
    setPreviewError(null);
    setUpstreamColumns([]);
    setColumnsError(null);
  }, [selectedNode.id]);

  const fetchUpstreamColumns = useCallback(async () => {
    setColumnsLoading(true);
    setColumnsError(null);
    try {
      // ADR-0076 cache-only read. Never triggers chain execution. Tries the
      // INIT port first, falls back to INCREMENTAL (per the persist-spec
      // mental model: INIT drives metadata). If neither port has a cached
      // schema (node_output_cache / live_fallback / input_snapshot_row), the
      // hub returns 404 `no_cached_schema` with a "run the upstream once"
      // message — we surface that as the columns error rather than running
      // anything from this panel.
      const data = await apiFetch<{
        columns?: Array<{ name?: string }>;
      }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/upstream-columns`,
      );
      const names = (data.columns ?? [])
        .map((c) => c.name)
        .filter((n): n is string => typeof n === "string" && n.trim().length > 0);
      setUpstreamColumns(names);
    } catch (e) {
      setColumnsError(e instanceof Error ? e.message : String(e));
    } finally {
      setColumnsLoading(false);
    }
  }, [operationId, selectedNode.id]);

  useEffect(() => {
    // ADR-0094: every keyed policy (append, merge, merge_history) needs the
    // upstream schema for the key picker. Snapshot doesn't.
    if (!KEYED_WRITE_MODES.has(writeMode)) return;
    void fetchUpstreamColumns();
  }, [writeMode, fetchUpstreamColumns]);

  /**
   * ADR-0100 — hydrate the form from spec_persist.config (or defaults when
   * none is set yet, e.g. a brand-new persist node). Replaces the prior
   * `syncFormFromPdo` which read from the PDO row.
   */
  function syncFormFromSpec(cfg: PdoConfig | undefined) {
    setWriteMode(cfg?.write_mode ?? "replace_snapshot");
    setPrimaryKeyFields([...(cfg?.business_key ?? [])].sort((a, b) => a.localeCompare(b)));
    setEventTimeField(cfg?.business_time?.source_field ?? "");
    setBusinessTimeFallback(cfg?.business_time?.fallback ?? "system_time");
    const pkLoaded = new Set(cfg?.business_key ?? []);
    const untracked = (cfg?.change_detection?.untracked_fields ?? []).filter((f) => !pkLoaded.has(f));
    setUntrackedFields([...untracked].sort((a, b) => a.localeCompare(b)));
    setMissingRowPolicy(cfg?.missing_row_policy ?? "ignore_missing");
    setLateArrivingPolicy(cfg?.late_arriving_policy ?? "reject");
    setDupHandling(cfg?.duplicate_key_handling ?? { mode: "error" });
    setMaxVersions(cfg?.retention?.max_versions != null ? String(cfg.retention.max_versions) : "");
    setMaxAgeDays(cfg?.retention?.max_age_days != null ? String(cfg.retention.max_age_days) : "");
    setColdMaxAgeDays(cfg?.retention?.cold_max_age_days != null ? String(cfg.retention.cold_max_age_days) : "");
    setRollbackCopies(cfg?.rollback_copies != null ? String(cfg.rollback_copies) : "");
  }

  // Build current config from form state (JSON keys unchanged for Hub: business_key, business_time, …)
  function buildConfig(overrides?: BuildOverrides): PdoConfig {
    const wm = overrides?.writeMode ?? writeMode;
    const pkFields = overrides?.primaryKeyFields ?? primaryKeyFields;
    const pkExclude = new Set(pkFields);
    const evt = overrides?.eventTimeField ?? eventTimeField;
    const btf = overrides?.businessTimeFallback ?? businessTimeFallback;
    const untrackedRaw = overrides?.untrackedFields ?? untrackedFields;
    const untrackedForConfig = untrackedRaw.filter((f) => !pkExclude.has(f));
    const dup = overrides?.dupHandling ?? dupHandling;

    const mrp = overrides?.missingRowPolicy ?? missingRowPolicy;
    const lap = overrides?.lateArrivingPolicy ?? lateArrivingPolicy;
    const mv = overrides?.maxVersions ?? maxVersions;
    const ma = overrides?.maxAgeDays ?? maxAgeDays;
    const mc = overrides?.coldMaxAgeDays ?? coldMaxAgeDays;

    const rbcRaw = overrides?.rollbackCopies ?? rollbackCopies;

    const cfg: PdoConfig = { version: "pdo.v1", write_mode: wm };
    if (KEYED_WRITE_MODES.has(wm)) {
      if (pkFields.length) cfg.business_key = pkFields;
      cfg.duplicate_key_handling = dup;
      // ADR-0103: rollback depth (keyed-only). Empty → omit (default 0).
      if (rbcRaw !== "") {
        const n = Number(rbcRaw);
        if (Number.isInteger(n) && n >= 0) cfg.rollback_copies = n;
      }
    }
    // ADR-0103: `merge` adopts the tracked-column change_detection deny-list.
    if (wm === "merge") {
      cfg.change_detection = { untracked_fields: untrackedForConfig };
    }
    if (wm === "merge_history") {
      if (evt.trim()) cfg.business_time = { source_field: evt.trim(), fallback: btf };
      cfg.change_detection = { untracked_fields: untrackedForConfig };
      cfg.missing_row_policy = mrp;
      cfg.late_arriving_policy = lap;
    }
    const rv = mv ? Number(mv) : null;
    const ra = ma ? Number(ma) : null;
    const rc = mc ? Number(mc) : null;
    if (rv !== null || ra !== null || rc !== null) {
      cfg.retention = { max_versions: rv, max_age_days: ra, cold_max_age_days: rc };
    }
    return cfg;
  }

  /**
   * ADR-0100 — mirror form state into `node.data.config.spec_persist.config`.
   * The canvas autosave path persists the spec like any other node config.
   */
  const writeSpecConfig = useCallback((next: PdoConfig) => {
    setNodeData(selectedNode.id, (d) => {
      const cfg = (d.config ?? {}) as Record<string, unknown>;
      const sp = ((cfg.spec_persist as Record<string, unknown>) ?? {}) as Record<string, unknown>;
      return { ...d, config: { ...cfg, spec_persist: { ...sp, config: next } } };
    });
  }, [selectedNode.id, setNodeData]);

  async function runPreview() {
    if (previewInFlightRef.current) return;
    previewInFlightRef.current = true;
    setPreviewBusy(true);
    setPreviewError(null);
    setMergeAnalysis(null);
    try {
      // ADR-0128 — the run is async and carries no payload. Trigger a fresh
      // upstream pull (forceRefresh so the next actual run's watermark window
      // is mirrored), then read the persist INPUT schema from the node cache
      // via the existing cache-only `/upstream-columns` endpoint.
      await runController.startRun({
        operationId,
        nodeId: selectedNode.id,
        forceRefresh: true,
        onComplete: async () => {
          await fetchUpstreamColumns();
          setColumnsError(null);
        },
        onError: (error) => { setPreviewError(error ?? "Run failed."); },
        onFailure: (err) => { setPreviewError(err instanceof Error ? err.message : String(err)); },
      });
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      previewInFlightRef.current = false;
      setPreviewBusy(false);
    }
  }

  /**
   * The id of the operation run this persist node is currently the target of.
   * Null when none is in flight or the lookup failed.
   */
  const findActivePersistRunId = useCallback(async (): Promise<string | null> => {
    try {
      const data = await apiFetch<{ runs: OperationRun[] }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/runs?active=1&limit=20`,
      );
      const mine = (data.runs ?? []).find(
        (r) => r.context === "execute_persist" && isRunTargetNode(r, selectedNode.id),
      );
      return mine?.id ?? null;
    } catch {
      return null;
    }
  }, [operationId, selectedNode.id]);

  /**
   * Poll a run until it settles, and report ITS outcome. Used when the browser
   * lost the `execute-persist` request: the run is server-side work that
   * outlives the request that started it, so the request dying is not news
   * about the load. Resolves null if the inspector unmounts, or if the run
   * becomes unreadable — never on a transient poll failure, which says as
   * little about the run as the dropped request did.
   */
  const followRunToTerminal = useCallback(async (runId: string): Promise<OperationRun | null> => {
    while (!disposedRef.current) {
      await sleep(RUN_FOLLOW_POLL_MS);
      if (disposedRef.current) return null;
      try {
        const detail = await apiFetch<{ run: OperationRun }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/runs/${encodeURIComponent(runId)}`,
        );
        if (isTerminalRunStatus(detail.run.status)) return detail.run;
      } catch (e) {
        // The run record is gone — nothing left to watch.
        if (e instanceof ApiRequestError && e.status === 404) return null;
        /* transient — keep watching; the run does not care about our socket */
      }
    }
    return null;
  }, [operationId]);

  async function runExecute(mode: "initialize" | "increment") {
    // Re-entry guard — block a second click before the `executing` disable
    // lands. We await flushAndSave() below before any render, so without this
    // a rapid double-click fires two materializations; the server then rejects
    // the second with the one-running-per-PDO constraint.
    if (runInFlightRef.current) return;
    // A run reported in flight by the server (started in this or another tab,
    // possibly before a remount) also blocks — the buttons disable on this, but
    // guard the non-button callers (rebuild banner) too.
    if (serverRunActive) return;
    runInFlightRef.current = true;
    setExecuting(true);
    // See the `execute-persist` POST below: the run id is discovered while the
    // request is open, and `postFired` scopes the lost-connection handling to
    // the one request that can leave a run behind.
    let discoveredRunId: string | null = null;
    let discovering = false;
    let postFired = false;
    // Hoisted out of the try so the lost-connection path below can name the
    // store it was loading, including one created moments ago in this call.
    let activePdoId = pdoId;
    try {

      // Bug fix 2026-06-10 — force the autosave debounce to land BEFORE we hit
      // the hub. Without this, an upstream SQL edit (or any other pending
      // canvas state) wouldn't yet be in the saved spec when the hub loaded
      // `operation.spec`, so the run executed against stale SQL — looked like
      // a cache hit but was actually a save-ordering race. flushAndSave returns
      // false on failure; abort the run so the user doesn't get a misleading
      // "succeeded" toast against the wrong spec.
      const flushed = await flushAndSave();
      if (!flushed) {
        toast.push({ kind: "error", message: "Couldn't save pending edits — fix the validation error above and try again." });
        return;
      }

      if (!activePdoId) {
        if (mode === "increment") {
          toast.push({ kind: "error", message: "Run Initialize first to create the data store." });
          return;
        }
        // ADR-0100 — flush the spec config to the canvas spec BEFORE the POST,
        // so the hub's spec-side read picks up the user's pending edits. Then
        // POST without body.config (hub reads from spec). The second
        // flushAndSave below pushes that just-written config to the server so
        // POST /tenant/pdos finds it.
        writeSpecConfig(buildConfig());
        const flushedAfterWrite = await flushAndSave();
        if (!flushedAfterWrite) {
          toast.push({ kind: "error", message: "Couldn't save pending edits — fix the validation error above and try again." });
          return;
        }
        try {
          const data = await apiFetch<{ pdo: PdoSummary }>("/api/tenant/pdos", {
            method: "POST",
            body: {
              source_operation_id: operationId,
              source_node_id: selectedNode.id,
            },
          });
          const created = data.pdo;
          activePdoId = created.id;
          setPdo(created);
          setNodeData(selectedNode.id, (d) => {
            const cfg = (d.config ?? {}) as Record<string, unknown>;
            const sp = ((cfg.spec_persist as Record<string, unknown>) ?? {}) as Record<string, unknown>;
            return { ...d, config: { ...cfg, spec_persist: { ...sp, pdo_id: created.id } } };
          });
        } catch (e) {
          toast.push({ kind: "error", message: `Failed to create data store: ${e instanceof Error ? e.message : String(e)}` });
          return;
        }
      }

      if (mode === "initialize" && pdo?.current_version) {
        const ok = await confirmCtx.confirm({
          title: "Re-initialize PDO",
          detail: "This will wipe all existing versions and reload from upstream with the new configuration.",
          confirmLabel: "Re-initialize",
        });
        if (!ok) return;
      }

      // The hub runs the whole materialization inside this request at
      // interactive priority and answers only when it is done — minutes, for a
      // large load. The run, though, is server-side work with its own record:
      // it keeps going after the call that started it returns. So discover the
      // run id WHILE the request is open, because once the browser drops the
      // connection there is nothing in the rejection to identify it by.
      postFired = true;
      discovering = true;
      void (async () => {
        for (let attempt = 0; attempt < RUN_DISCOVERY_MAX_ATTEMPTS; attempt += 1) {
          if (!discovering || disposedRef.current || discoveredRunId !== null) return;
          discoveredRunId = await findActivePersistRunId();
          if (discoveredRunId !== null) return;
          await sleep(RUN_DISCOVERY_POLL_MS);
        }
      })();

      const data = await apiFetch<{
        materialization: {
          status: string;
          version_number: number;
          row_count: number | null;
          duration_ms: number | null;
          merge_summary: {
            rows_read?: number | null;
            rows_new?: number | null;
            rows_changed?: number | null;
            rows_unchanged?: number | null;
            rows_skipped?: number | null;
          } | null;
        };
      }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/execute-persist`,
        {
          method: "POST",
          body: {
            mode,
            // Pass the freshly-created (or known) pdo_id so the server can use it
            // even before the spec autosave has flushed to the DB.
            pdo_id: activePdoId ?? undefined,
          },
        }
      );
      discovering = false;
      const m = data.materialization;
      toast.push({ kind: "success", message: `v${m.version_number} — ${(m.row_count ?? 0).toLocaleString()} rows` });
      if (activePdoId) {
        try {
          const refreshed = await apiFetch<{ pdo: PdoSummary }>(`/api/tenant/pdos/${encodeURIComponent(activePdoId)}`);
          setPdo(refreshed.pdo);
        } catch { /* non-critical */ }
      }
    } catch (e) {
      discovering = false;
      // The hub ANSWERED with a refusal — report it as written. Only the
      // execution codes are an execution failure; "a run is already in
      // progress" is not, and prefixing it made it read like one.
      if (!isTransportFailure(e)) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.push({
          kind: "error",
          message: e instanceof ApiRequestError && e.code === "materialization_running"
            ? msg
            : `Execution failed: ${msg}`,
        });
        return;
      }
      // The REQUEST died, not the run. A persist load holds its request open
      // for the whole materialization and the browser gives up long before the
      // hub does — so report the RUN's fate, never the request's.
      //
      // Defect, 2026-09-13: this handler read `Execution failed: Failed to
      // fetch` while the run was alive and heartbeating, and it ran for another
      // six minutes after the toast. Everything downstream of that toast was a
      // diagnosis of the wrong thing.
      if (!postFired) {
        toast.push({ kind: "error", message: "Couldn't reach the server. Nothing was started — check your connection and try again." });
        return;
      }
      const runId = discoveredRunId ?? (await findActivePersistRunId());
      if (!runId) {
        toast.push({
          kind: "info",
          message: "Lost the connection while the load was running. The load may still be going on the server — Run History below shows how it ends.",
        });
        return;
      }
      toast.push({
        kind: "info",
        message: "Lost the connection to the server. The load is still running — watching it from here.",
      });
      const finished = await followRunToTerminal(runId);
      if (!finished) {
        // Unmounted, or the run became unreadable. Never invent an outcome —
        // the run log below is the record either way.
        if (!disposedRef.current) {
          toast.push({ kind: "info", message: "Lost track of the load. Run History below shows how it ended." });
        }
        return;
      }
      if (finished.status === "succeeded") {
        // Re-read the store so the version the load produced can be named, the
        // same way the normal success path names it.
        let landed: PdoSummary | null = null;
        if (activePdoId) {
          try {
            landed = (await apiFetch<{ pdo: PdoSummary }>(`/api/tenant/pdos/${encodeURIComponent(activePdoId)}`)).pdo;
            if (!disposedRef.current) setPdo(landed);
          } catch { /* non-critical */ }
        }
        const cv = landed?.current_version ?? null;
        toast.push({
          kind: "success",
          message: cv
            ? `The load finished — v${cv.version_number}, ${cv.row_count.toLocaleString()} rows.`
            : "The load finished.",
        });
      } else if (finished.status === "cancelled") {
        toast.push({ kind: "info", message: "The load was cancelled." });
      } else {
        toast.push({ kind: "error", message: `The load failed: ${finished.error ?? "no reason recorded"}` });
      }
    } finally {
      discovering = false;
      runInFlightRef.current = false;
      setExecuting(false);
      // Refresh Last Execution / Run History / watermark History once the run
      // completes — success OR failure — so a failed run (e.g. a schema
      // mismatch) appears without a manual refresh. PersistRunHistory and
      // WatermarkStateSection both re-fetch on this nonce.
      setWmRefreshNonce((n) => n + 1);
    }
  }

  /**
   * Sticky "INIT / INCREMENTAL on next run" toggle for keyed PDOs that have
   * NO watermark binding — those don't get the WatermarkStateSection "State"
   * box (which owns the same flip for watermark PDOs). Sets the PDO's
   * `current_run_mode` WITHOUT running anything; the next scheduled / manual
   * run consumes it (and the runtime flips INIT back to increment after a
   * successful rebuild). Wording mirrors the watermark box so there's one
   * vocabulary across all keyed PDOs.
   */
  async function runSetNextRunMode(target: "initialize" | "increment") {
    if (!pdoId) return;
    if (target === "initialize") {
      const pdoName = pdo?.name?.trim() || selectedNode.data.label?.trim() || "this data store";
      const ok = await confirmCtx.confirm({
        title: "Switch to INIT mode",
        detail: "The next run of this persist will truncate all existing data and rebuild from scratch. Nothing runs now.",
        confirmLabel: "Switch to INIT",
        destructive: true,
        requireTypedConfirmation: { value: pdoName, label: "Type the data store name to confirm:" },
      });
      if (!ok) return;
    }
    setModeBusy(true);
    try {
      const path =
        target === "initialize"
          ? `/api/tenant/pdos/${encodeURIComponent(pdoId)}/mark-for-reinitialize`
          : `/api/tenant/pdos/${encodeURIComponent(pdoId)}/clear-reinitialize`;
      await apiFetch(path, { method: "POST" });
      // Re-read the enriched PDO shape (current_version + spec_drift) so the
      // "Next run mode" banner updates. The mark/clear endpoints return a bare
      // PDO row without those computed fields, so don't setPdo from them.
      await refreshPdo();
      toast.push({
        kind: "success",
        message: target === "initialize" ? "Next run will re-initialize." : "Next run will increment.",
      });
    } catch (e) {
      toast.push({
        kind: "error",
        message: `Couldn't change next-run mode: ${e instanceof Error ? e.message : String(e)}`,
      });
    } finally {
      setModeBusy(false);
    }
  }

  const hasVersions = (pdo?.current_version?.version_number ?? 0) > 0;

  // ADR-0102 — unified rebuild reasons. The banner replaces ADR-0100's
  // standalone drift banner + ADR-0095's red INIT-mode banner. Reasons are
  // computed client-side from the PDO summary; the hub records the
  // server-side authoritative reason on `pdo_materializations.reinitialize_reason`
  // when the rebuild actually runs (Phase 3 migration adds the column).
  // Any keyed PDO (append/merge/merge_history, with or without watermark
  // binding) whose persisted state says "next run is INIT". The mode lives
  // on the PDO row, not on the watermark state — the watermark is a cursor
  // that gets reset on INIT and advanced on INCREMENT (migration 159
  // consolidated this away from `pdo_watermark_state.mode`).
  const isKeyedConfig =
    writeMode === "append" || writeMode === "merge" || writeMode === "merge_history";
  // The big PersistRebuildBanner is reserved for genuine "you need to rebuild"
  // states: config drift vs the data store, or a never-initialized PDO. Being
  // in INIT mode is NOT one of these — it gets the plain status banner below.
  const rebuildReasons: RebuildReason[] = (() => {
    const reasons: RebuildReason[] = [];
    if (pdoId && pdo && !hasVersions) reasons.push("first_run");
    if (pdo?.spec_drift?.has_drift) reasons.push("drift");
    return reasons;
  })();
  const rebuildChanges: ChangeRow[] = pdo?.spec_drift?.has_drift ? pdo.spec_drift.changes : [];
  // A run is "active" if this instance kicked one off (`executing`) OR the server
  // reports one in flight (`serverRunActive`, from the run-log poll). Either one
  // disables the run/mode/preview controls so a second run can't be started.
  const runActive = executing || serverRunActive;
  const showRebuildBanner = rebuildReasons.length > 0 && !runActive;
  const hideStandaloneInit = showRebuildBanner;
  // Plain status banner: what mode the NEXT run will use. Keyed PDOs only
  // (snapshot has no INIT/INCREMENT distinction). A keyed PDO with versions
  // and current_run_mode='increment' runs INCREMENTAL; everything else
  // (init mode set, or brand-new / no versions yet) runs INIT.
  const nextRunModeLabel: "INIT" | "INCREMENTAL" | null = !isKeyedConfig
    ? null
    : hasVersions && pdo?.current_run_mode === "increment"
      ? "INCREMENTAL"
      : "INIT";
  const versionLabel = pdo?.current_version
    ? `v${pdo.current_version.version_number}, ${pdo.current_version.row_count.toLocaleString()} rows`
    : "No versions yet";

  // The "State" box (WatermarkStateSection) only exists for an already-created
  // watermark-bound keyed PDO. When it's shown, the standalone Run section is
  // redundant: run/preview move INTO the State box. For brand-new / snapshot /
  // non-watermark persists (no State box), the Run section stays.
  const showStateBox = Boolean(
    pdoId && pdo && isKeyedConfig && (specPersist as Record<string, unknown>)?.watermark,
  );

  // Run/preview result block — last-execution stats + merge analysis. Rendered
  // inside the State box when it's shown, else inline under the Run section.
  const resultsBlock = (
    <>
      {previewError ? (
        <div className="muted" style={{ fontSize: 12, color: "var(--danger-text)" }}>
          Preview failed: {previewError}
        </div>
      ) : null}

      {/* ADR-0103: persistent Last Execution + the job Run History, loaded from
          pdo_materializations. Replaces the prior ephemeral last-execution block
          (which vanished on node reselect and showed the last *version-producing*
          run rather than the actual last run). Re-fetches on wmRefreshNonce,
          which the run handler bumps. */}
      {pdoId ? (
        <PersistRunHistory
          pdoId={pdoId}
          writeMode={writeMode}
          currentVersionNumber={pdo?.current_version?.version_number ?? null}
          refreshNonce={wmRefreshNonce}
          onActiveChange={handleRunActiveChange}
          externallyActive={executing}
          collapsible
        />
      ) : null}

      {mergeAnalysis ? (
        <div className="field">
          <label className="label">Merge Analysis</label>
          {mergeAnalysis.error ? (
            <div className="muted" style={{ fontSize: 12, color: "var(--danger-text)" }}>{mergeAnalysis.error}</div>
          ) : (
            <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }} title="Rows the upstream produced for this run (the merge input)">
                <span className="muted">Rows from upstream</span>
                <span>{(mergeAnalysis.rows_read ?? 0).toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="muted">New rows</span>
                <span>{(mergeAnalysis.rows_new ?? 0).toLocaleString()}</span>
              </div>
              {mergeAnalysis.write_mode === "merge_history" ? (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="muted">Changed</span>
                    <span>{(mergeAnalysis.rows_changed ?? 0).toLocaleString()}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="muted">Unchanged</span>
                    <span>{(mergeAnalysis.rows_unchanged ?? 0).toLocaleString()}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="muted">Closed</span>
                    <span>{(mergeAnalysis.rows_closed ?? 0).toLocaleString()}</span>
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <InspectorTabs
        surfaceId="operationEditor"
        tabGroupId="persist"
        defaultSlot="setup"
        tabs={[
          {
            slot: "info",
            content: (
              <div className="field">
                <label className="label">Node label</label>
                <input
                  className="input"
                  value={selectedNode.data.label}
                  onChange={(e) => setNodeData(selectedNode.id, (d) => ({ ...d, label: e.target.value }))}
                  onBlur={() => void flushAndSave()}
                  placeholder="Unnamed"
                />
              </div>
            ),
          },
          {
            slot: "setup",
            content: (
    // minmax(0, 1fr): keep every inspector row sized to the panel frame so a
    // wide child (e.g. the Run History table) scrolls WITHIN its own control
    // instead of widening the inspector. Never an inspector-level scrollbar.
    <div style={{ display: "grid", gap: 14, gridTemplateColumns: "minmax(0, 1fr)" }}>

      {showRebuildBanner ? (
        <PersistRebuildBanner
          reasons={rebuildReasons}
          changes={rebuildChanges}
          onReinitialize={() => void runExecute("initialize")}
          disabled={runActive}
        />
      ) : null}

      {/* Plain status banner — what mode the next run will use. Both modes. */}
      {nextRunModeLabel ? (
        <div
          style={{
            padding: "6px 10px",
            fontSize: 12,
            borderRadius: 4,
            background:
              nextRunModeLabel === "INIT" ? "var(--color-warn-bg, #fff7ed)" : "var(--bg-subtle, #f3f4f6)",
            color:
              nextRunModeLabel === "INIT" ? "var(--color-warn-text, #9a3412)" : "var(--text-muted, #555)",
            border: `1px solid ${
              nextRunModeLabel === "INIT" ? "var(--color-warn-border, #fdba74)" : "var(--border, #ddd)"
            }`,
          }}
        >
          Next run mode: <strong>{nextRunModeLabel}</strong>
        </div>
      ) : null}

      {/*
        ADR-0100: form is always editable. Edits flush into
        spec_persist.config via writeSpecConfig (the canvas autosave path
        persists it like any other node config). Drift between the live
        spec and the PDO's snapshot is surfaced by the banner above.
       */}
      {(() => {
        const controlsDisabled = pdoLoading;
        return (
          <>
          <div className="field">
            <label className="label">Write Mode</label>
            <select
              className="select"
              value={writeMode}
              onChange={(e) => {
                const next = e.target.value as WriteMode;
                setWriteMode(next);
                writeSpecConfig(buildConfig({ writeMode: next }));
              }}
              onBlur={() => writeSpecConfig(buildConfig())}
              disabled={controlsDisabled}
            >
              <option value="replace_snapshot">{WRITE_MODE_LABELS.replace_snapshot}</option>
              <option value="append">{WRITE_MODE_LABELS.append}</option>
              <option value="merge">{WRITE_MODE_LABELS.merge}</option>
              <option value="merge_history">{WRITE_MODE_LABELS.merge_history}</option>
            </select>
            <div className="help" style={{ marginTop: 4 }}>
              {WRITE_MODE_DESCRIPTIONS[writeMode]}
            </div>
          </div>

          {/* Keyed policies (append, merge, merge_history) — key picker + within-batch dup. */}
          {KEYED_WRITE_MODES.has(writeMode) ? (
            <div style={{ display: "grid", gap: 10, paddingLeft: 2, borderLeft: "2px solid var(--border)" }}>
              <MergeHistoryFieldPickers
                columnNames={upstreamColumns}
                columnsLoading={columnsLoading}
                columnsError={columnsError}
                onRefreshColumns={() => void fetchUpstreamColumns()}
                refreshDisabled={pdoLoading}
                primaryKeyFields={primaryKeyFields}
                onPrimaryKeyChange={(next) => {
                  setPrimaryKeyFields(next);
                  writeSpecConfig(buildConfig({ primaryKeyFields: next }));
                }}
                businessEventTimeField={eventTimeField}
                onBusinessEventTimeField={(f) => {
                  setEventTimeField(f);
                  writeSpecConfig(buildConfig({ eventTimeField: f }));
                }}
                businessEventTimeFallback={businessTimeFallback}
                onBusinessEventTimeFallback={(v) => {
                  setBusinessTimeFallback(v);
                  writeSpecConfig(buildConfig({ businessTimeFallback: v }));
                }}
                untrackedFields={untrackedFields}
                onUntrackedFieldsChange={(next) => {
                  setUntrackedFields(next);
                  writeSpecConfig(buildConfig({ untrackedFields: next }));
                }}
                disabled={controlsDisabled}
                /** Business-event-time + fallback are merge_history-only. */
                showHistoryOnlyFields={writeMode === "merge_history"}
                /** ADR-0103: the tracked-columns picker applies to merge too. */
                showChangeTracking={writeMode === "merge" || writeMode === "merge_history"}
              />

              {primaryKeyFields.length === 0 ? (
                <div className="help" style={{ color: "var(--danger-text)" }}>
                  Pick at least one key column.
                </div>
              ) : null}

              <WithinBatchDupSection
                columnNames={upstreamColumns}
                keyFields={primaryKeyFields}
                value={dupHandling}
                onChange={(next) => {
                  setDupHandling(next);
                  writeSpecConfig(buildConfig({ dupHandling: next }));
                }}
                disabled={controlsDisabled}
              />

              {/* ADR-0095: Watermark binding subsection. Lives at
                  spec_persist.watermark — distinct from spec_persist.config
                  which carries the rest of the policy. */}
              <WatermarkBindingSection
                binding={
                  (specPersist as Record<string, unknown>)?.watermark
                    ? ((specPersist as Record<string, unknown>).watermark as WatermarkBinding)
                    : null
                }
                operationVariables={operationVariables}
                upstreamColumns={upstreamColumns}
                onChange={(next) => {
                  setNodeData(selectedNode.id, (d) => {
                    const cfg = (d.config ?? {}) as Record<string, unknown>;
                    const sp = ((cfg.spec_persist as Record<string, unknown>) ?? {}) as Record<string, unknown>;
                    const { watermark: _drop, ...rest } = sp;
                    void _drop;
                    const newSp: Record<string, unknown> = next ? { ...rest, watermark: next } : { ...rest };
                    return { ...d, config: { ...cfg, spec_persist: newSp } };
                  });
                }}
              />

              <div className="field">
                <label className="label">NULL key handling</label>
                <div className="help">
                  Rows with NULL values in any key column are rejected. Use a Transform node upstream (Coalesce or Filter) if you need to handle missing keys.
                </div>
              </div>

              {writeMode === "merge_history" ? (
                <>
                  <div className="field">
                    <label className="label">Missing Row Policy</label>
                    <select
                      className="select"
                      value={missingRowPolicy}
                      onChange={(e) => {
                        const v = e.target.value as "ignore_missing" | "close_on_missing";
                        setMissingRowPolicy(v);
                        writeSpecConfig(buildConfig({ missingRowPolicy: v }));
                      }}
                      disabled={controlsDisabled}
                    >
                      <option value="ignore_missing">{MISSING_ROW_POLICY_LABELS.ignore_missing}</option>
                      <option value="close_on_missing">{MISSING_ROW_POLICY_LABELS.close_on_missing}</option>
                    </select>
                  </div>
                  <div className="field">
                    <label className="label">Late-arriving rows</label>
                    <select
                      className="select"
                      value={lateArrivingPolicy}
                      onChange={(e) => {
                        const v = e.target.value as "reject" | "allow_monotonic_only";
                        setLateArrivingPolicy(v);
                        writeSpecConfig(buildConfig({ lateArrivingPolicy: v }));
                      }}
                      disabled={controlsDisabled}
                    >
                      <option value="reject">{LATE_ARRIVING_LABELS.reject}</option>
                      <option value="allow_monotonic_only">{LATE_ARRIVING_LABELS.allow_monotonic_only}</option>
                    </select>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
          </>
        );
      })()}

      <details
        open={retentionOpen ?? isKeyedConfig}
        onToggle={(e) => setRetentionOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--muted)", userSelect: "none" }}>
          {isKeyedConfig ? "Rollback" : "Retention"}
        </summary>
        <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
          {isKeyedConfig ? (
            // ADR-0103: keyed stores maintain a SINGLE current copy — the
            // warm/cold version knobs don't apply. Retention is bounded by
            // rollback_copies (current + N restorable rollback points).
            <div className="field">
              <label className="label">Rollback copies</label>
              <span className="help" style={{ display: "block", marginBottom: 6 }}>
                This store keeps one current copy. Set how many recent prior copies to retain as
                restorable rollback points. 0 = current only.
              </span>
              <input
                className="input"
                type="number"
                value={rollbackCopies}
                onChange={(e) => setRollbackCopies(e.target.value)}
                onBlur={(e) => writeSpecConfig(buildConfig({ rollbackCopies: e.target.value }))}
                placeholder="0"
                min={0}
              />
            </div>
          ) : (
            <>
              <div className="field">
                <label className="label">Max Warm Versions</label>
                <input
                  className="input"
                  type="number"
                  value={maxVersions}
                  onChange={(e) => setMaxVersions(e.target.value)}
                  onBlur={(e) => writeSpecConfig(buildConfig({ maxVersions: e.target.value }))}
                  placeholder="unlimited"
                  min={1}
                />
              </div>
              <div className="field">
                <label className="label">Max Warm Age (Days)</label>
                <input
                  className="input"
                  type="number"
                  value={maxAgeDays}
                  onChange={(e) => setMaxAgeDays(e.target.value)}
                  onBlur={(e) => writeSpecConfig(buildConfig({ maxAgeDays: e.target.value }))}
                  placeholder="unlimited"
                  min={1}
                />
              </div>
              <div className="field">
                <label className="label">Cold Retention (Days)</label>
                <input
                  className="input"
                  type="number"
                  value={coldMaxAgeDays}
                  onChange={(e) => setColdMaxAgeDays(e.target.value)}
                  onBlur={(e) => writeSpecConfig(buildConfig({ coldMaxAgeDays: e.target.value }))}
                  placeholder="keep forever"
                  min={1}
                />
              </div>
            </>
          )}
        </div>
      </details>

      {/* Standalone Run section — only when there's no State box (brand-new /
          snapshot / non-watermark persists). The State box absorbs run +
          preview for watermark-bound keyed PDOs. */}
      {!showStateBox ? (
        <>
          <div className="field">
            <label className="label">Run</label>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              {pdoLoading ? "Loading…" : versionLabel}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {!hideStandaloneInit ? (
                <button
                  className={`btn btnSm${hasVersions ? " btnDanger" : " btnPrimary"}`}
                  type="button"
                  onClick={() => void runExecute("initialize")}
                  disabled={runActive || previewBusy}
                  title={previewBusy ? "Preview in progress. Resumes when it finishes." : undefined}
                >
                  {runActive ? "Running…" : hasVersions ? "Re-initialize" : "Initialize"}
                </button>
              ) : null}
              <button
                className="btn btnSm btnPrimary"
                type="button"
                onClick={() => void runExecute("increment")}
                disabled={runActive || previewBusy || !hasVersions}
                title={previewBusy ? "Preview in progress. Resumes when it finishes." : !pdoId ? "Run Initialize first" : !hasVersions ? "Initialize must succeed before incrementing" : undefined}
              >
                {runActive ? "Running…" : "Increment"}
              </button>
              <button
                className="btn btnSm"
                type="button"
                onClick={() => void runPreview()}
                disabled={previewBusy || runActive}
                title={runActive ? "Run in progress. Preview resumes when it finishes." : undefined}
              >
                {previewBusy ? "Previewing…" : "Preview"}
              </button>
            </div>
            {/* Sticky next-run-mode toggle — keyed PDOs WITHOUT a watermark
                binding only (watermark PDOs get the same flip in the State
                box). Queues an INIT (or backs one out) for the next
                scheduled / manual run without firing a rebuild from here.
                Only meaningful once versions exist; a versionless keyed PDO
                already runs INIT next regardless. */}
            {isKeyedConfig && pdoId && pdo && hasVersions ? (
              <div style={{ marginTop: 8 }}>
                {pdo.current_run_mode === "initialize" ? (
                  <button
                    className="btn btnSm"
                    type="button"
                    onClick={() => void runSetNextRunMode("increment")}
                    disabled={runActive || modeBusy || previewBusy}
                    title="Next run will rebuild from scratch. Switch it back to incremental without running."
                  >
                    {modeBusy ? "Saving…" : "Switch to INCREMENTAL mode"}
                  </button>
                ) : (
                  <button
                    className="btn btnSm btnDanger"
                    type="button"
                    onClick={() => void runSetNextRunMode("initialize")}
                    disabled={runActive || modeBusy || previewBusy}
                    title="Queue a full rebuild for the next run, without running it now."
                  >
                    {modeBusy ? "Saving…" : "Switch to INIT mode"}
                  </button>
                )}
              </div>
            ) : null}
          </div>
          {resultsBlock}
        </>
      ) : null}

      {/* ADR-0095: Watermark "State" box. For watermark-bound keyed PDOs it now
          owns run (Run now) + preview + the run-result block; the standalone
          Run section above is suppressed (showStateBox). The component returns
          null on 404 / no binding state row. */}
      {showStateBox && pdo && pdoId ? (
        <WatermarkStateSection
          pdoId={pdoId}
          // Real, human data-store name — never the raw UUID (which the
          // typed-confirm echoes into the dialog).
          pdoName={pdo.name?.trim() || selectedNode.data.label?.trim() || "this data store"}
          hasRunningMaterialization={runActive}
          onRunNow={() => runExecute("increment")}
          refreshNonce={wmRefreshNonce}
          onModeChanged={() => void refreshPdo()}
          onPreview={() => void runPreview()}
          previewBusy={previewBusy}
        >
          {resultsBlock}
        </WatermarkStateSection>
      ) : null}

      {pdoId ? (
        <Link
          // Carry a returnTo back to THIS operation + persist node, so the PDO
          // detail page shows a "← Back to …" banner (same pattern as the
          // page→operation drill-in). returnLabel is resolved on the detail
          // page from the PDO's source operation name.
          to={`/app/build/pdos/${encodeURIComponent(pdoId)}?returnTo=${encodeURIComponent(
            `/app/build/operations/${operationId}?node=${selectedNode.id}`,
          )}`}
          style={{ fontSize: 12 }}
        >
          View data store details →
        </Link>
      ) : null}
    </div>
            ),
          },
        ]}
      />
    </div>
  );
}
